import React, { useEffect } from "react";
import { Redirect, Route, Switch } from "react-router-dom";
import { BookingSearchPage } from "./bookings/BookingSearchPage";
import { ConsultantSearchPage } from "./consultants/ConsultantSearchPage";
import { CustomerSearchPage } from "./customers/CustomerSearchPage";
import { AccommodationSearchPage } from "./accommodations/AccommodationSearchPage";
import { ConsultantImportPage } from "./consultants_import/ConsultantImportPage";
import SharedFilePage from "./shared_files/SharedFilePage";
import CustomerContractsPage from "@contracts/CustomerContractsPage";
import TicketsSearch from "@tickets/TicketSearch/TicketsSearchPage";
import { ConfigEditor } from "./config_editor/ConfigEditor";
import { forms } from "./forms/forms";
import { TimereportKitFlowSearchPage } from "./TimereportKitFlow/TimereportKitFlowSearchPage";
import { ContractViewerWrapper } from "./contractViewer/ContractViewerWrapper";
import { LogoutPage } from "./logout/logout-page";
import { ConsultantPipelinePage } from "./consultant-pipeline/consultant-pipeline.page";
import { WorkflowStatusPage } from "./workflow-status/workflow-status-page";
import { BookingKitSearchPage } from "./booking-kits/booking-kit-search-page";
import { AccommodationRequestSearchPage } from "./accommodation-requests/accommodation-request-search-page";
import { TravelRequestSearchPage } from "./travel-requests/travel-request-search-page";
import { ErpWrDraftsPage } from "./erp/erp-wr-drafts-page";
import { useGlobalStore, useNavManager, useTenant } from "@hooks";
import {
  BOOKING_SLIDER,
  BookingProfileLocation,
  CONSULTANT_CONTRACT_SLIDER,
  ConsultantContractEditorLocation,
  CONTRACT_SLIDER,
  ConsultantEditorLocation,
  ConsultantProfileLocation,
  ContractViewLocation,
  CUSTOMER_CONTRACT_SLIDER,
  CustomerEditorLocation,
  CustomerProfileLocation,
} from "@navigation";
import { CustomerContractProfileLocation } from "./contracts/locations/CustomerContractProfileLocation";
import { ErpContractPrefill } from "@consultants/ConsultantContract/erpPrefill";
import { filterItems } from "@containers/MainMenu/MainMenu";

type EventMessageData =
  | { type: "consultant"; id: string }
  | { type: "customer"; id: string }
  | { type: "booking"; id: string }
  | { type: "consultant-contract"; id: string }
  | { type: "customer-contract"; id: string }
  | { type: "consultant-new" }
  | { type: "customer-new" }
  | {
      type: "consultant-contract-new";
      id: string;
      prefill?: ErpContractPrefill;
    };

const NoAccessPage: React.FC = () => (
  <div style={{ textAlign: "center", marginTop: "20vh" }}>
    <h2>Access Restricted</h2>
    <p>You do not have permission to access this page.</p>
    <a href="/logout">Logout</a>
  </div>
);

export const Routes = () => {
  const { user } = useGlobalStore();
  const { tenant } = useTenant();
  const { navManager } = useNavManager();

  useEffect(() => {
    const handleMessage = (message: MessageEvent) => {
      if (message.data?.eventName !== "open-external-entity") {
        return;
      }

      const messageData = message.data as EventMessageData;

      if (messageData.type === "consultant") {
        navManager.open("consultants", new ConsultantProfileLocation(messageData.id));
      } else if (messageData.type === "customer") {
        navManager.open("customers", new CustomerProfileLocation(messageData.id));
      } else if (messageData.type === "booking") {
        navManager.open(BOOKING_SLIDER, new BookingProfileLocation(messageData.id));
      } else if (messageData.type === "consultant-contract") {
        // The viewer resolves the contract variant from the id itself, so both
        // consultant contract kinds come through this one slider.
        navManager.open(CONTRACT_SLIDER, new ContractViewLocation(messageData.id));
      } else if (messageData.type === "customer-contract") {
        navManager.open(
          CUSTOMER_CONTRACT_SLIDER,
          new CustomerContractProfileLocation(messageData.id)
        );
      } else if (messageData.type === "consultant-contract-new") {
        // ERP-741 — the ERP starts a consultant contract from a work request
        // and hands over what it already knows. `id` is the consultant; the
        // contract does not exist yet, so the editor opens on a blank one.
        navManager.open(
          CONSULTANT_CONTRACT_SLIDER,
          new ConsultantContractEditorLocation(
            messageData.id,
            "",
            "add",
            messageData.prefill
          )
        );
      } else if (messageData.type === "consultant-new") {
        navManager.open("consultants", new ConsultantEditorLocation(null));
      } else if (messageData.type === "customer-new") {
        navManager.open("customers", new CustomerEditorLocation(null));
      } else {
        console.warn("Unknown entity type:", (messageData as EventMessageData).type);
      }
    };

    window.addEventListener("message", handleMessage);

    return () => {
      window.removeEventListener("message", handleMessage);
    };
  }, []);

  const canAccessTenant = tenant === "wcc" ? user.can("tenant-wcc:read:all") : true; //todo update when add more tenants

  const canAccessMenuItem = filterItems(
    { path: window.location.pathname, label: "" },
    user.myProfile.Permissions,
    (perm) => user.can(perm)
  );

  // 🧠 If tenant access is restricted, redirect to a custom page
  if (!canAccessTenant) {
    console.log("Redirecting to canAccessTenant");
    return (
      <Switch>
        <Route path="/no-access" component={NoAccessPage} />
        <Route path="/logout" component={LogoutPage} />
        <Redirect to="/no-access" />
      </Switch>
    );
  }

  if (!canAccessMenuItem) {
    console.log("Redirecting to canAccessMenuItem");
    return (
      <Switch>
        <Route path="/no-access" component={NoAccessPage} />
        <Route path="/logout" component={LogoutPage} />
        <Redirect to="/no-access" />
      </Switch>
    );
  }

  return (
    <Switch>
      <Route path="/accommodations" component={AccommodationSearchPage} />
      <Route path="/accommodation_requests" component={AccommodationRequestSearchPage} />
      <Route path="/travel_requests" component={TravelRequestSearchPage} />
      <Route path="/bookings" component={BookingSearchPage} />
      <Route path="/timereport_kit_flow" component={TimereportKitFlowSearchPage} />
      <Route path="/kits" component={BookingKitSearchPage} />
      <Route path="/consultant_import" component={ConsultantImportPage} />
      <Route path="/consultants" component={ConsultantSearchPage} />
      <Route path="/contract" component={ContractViewerWrapper} />
      <Route path="/customer_contracts" component={CustomerContractsPage} />
      <Route path="/customers" component={CustomerSearchPage} />
      <Route path="/dashboard/tickets" component={TicketsSearch} />
      <Route path="/documents" component={SharedFilePage} />
      <Route path="/config_editor/:tab?" component={ConfigEditor} />
      <Route path="/forms" component={forms} />
      <Route path="/logout" component={LogoutPage} />
      <Route path="/pipeline" component={ConsultantPipelinePage} />
      <Route path="/workflow_status" component={WorkflowStatusPage} />
      <Route path="/demand/:id?" component={ErpWrDraftsPage} />

      <Redirect to="/dashboard/tickets" />
    </Switch>
  );
};

export default Routes;
